import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { submitEnquiry } from "../utils/submitEnquiry";
import {
    canSubmitEnquiryForm,
    sanitizeIndianMobileInput,
} from "../utils/phoneValidation";
import { LANDMARKS_PHONE_TEL, openTelDialer } from "../constants/contactLinks";
import {
    FaHome,
    FaChartLine,
    FaFileContract,
    FaMapMarkedAlt,
    FaUserTie,
    FaKey,
    FaCalculator,
    FaShieldAlt,
    FaClipboardCheck,
    FaBuilding
} from "react-icons/fa";

export default function Services() {
    const [contactOpen, setContactOpen] = useState(false);
    const [callbackForm, setCallbackForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [interestOpen, setInterestOpen] = useState(false);
    const [interest, setInterest] = useState("I'm interested in");
    const [enquirySubmitting, setEnquirySubmitting] = useState(false);
    const callbackFormValid = canSubmitEnquiryForm(callbackForm);

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
        window.scrollTo(0, 0);
    }, []);

    const mainServices = [
        {
            icon: <FaHome />,
            title: "Property Buying Advisory",
            desc: "Purchases are guided by foresight, not listings. This service centers on aligning intent with locations and assets that remain relevant over time.",
            features: [
                "Asset relevance screening beyond demand",
                "Location longevity evaluation",
                "Price-to-potential alignment checks",
                "Purchase-stage risk filtering"
            ]
        },
        {
            icon: <FaKey />,
            title: "Property Selling",
            desc: "Value is shaped before it is presented. We refine positioning so your property enters the market with purpose and exits with justification, not compromise.",
            features: [
                "Market entry timing assessment",
                "Buyer intent qualification",
                "Value narrative structuring",
                "Exit positioning support"
            ]
        },
        {
            icon: <FaChartLine />,
            title: "Investment Advisory",
            desc: "Capital moves best with discipline. This advisory focuses on identifying assets that mature steadily, protect downside, and reward patience over speculation.",
            features: [
                "Capital exposure review",
                "Growth curve mapping",
                "Risk-weighted asset comparison",
                "Long-hold viability checks"
            ]
        },
        {
            icon: <FaBuilding />,
            title: "Commercial Assets",
            desc: "Commercial choices carry consequences. We assess operational fit, demand continuity, and resilience before recommending any holding.",
            features: [
                "Usage suitability analysis",
                "Tenant demand stability review",
                "Income behavior assessment",
                "Commercial lifecycle evaluation"
            ]
        },
        {
            icon: <FaFileContract />,
            title: "Legal & Documentation",
            desc: "Clarity lives in documentation. This service exists to eliminate ambiguity, align records, and secure ownership with precision rather than assumption.",
            features: [
                "Ownership trail validation",
                "Documentation consistency checks",
                "Compliance alignment review",
                "Possession readiness verification"
            ]
        },
        {
            icon: <FaMapMarkedAlt />,
            title: "Site Surveys & Due Diligence",
            desc: "Reality is verified on ground, not paper. We validate dimensions, boundaries, and site conditions so decisions are made on facts, not projections.",
            features: [
                "Physical accuracy confirmation",
                "Boundary clarity checks",
                "Site usability assessment",
                "Ground condition validation"
            ]
        }
    ];

    const additionalServices = [
        {
            icon: <FaCalculator />,
            title: "Home Loan Assistance",
            desc: "Connect with trusted financial institutions for competitive home loan rates"
        },
        {
            icon: <FaClipboardCheck />,
            title: "Property Valuation",
            desc: "Accurate market valuations by certified professionals"
        },
        {
            icon: <FaBuilding />,
            title: "Construction Consultation",
            desc: "Expert advice on construction planning and execution"
        }
    ];

    const process = [
        {
            step: "01",
            title: "Initial Consultation",
            desc: "We understand your requirements, budget, and preferences in detail."
        },
        {
            step: "02",
            title: "Property Search",
            desc: "Our team expertly curates the best properties matching your criteria."
        },
        {
            step: "03",
            title: "Site Visits",
            desc: "We arrange and accompany you on property visits and inspections."
        },
        {
            step: "04",
            title: "Documentation",
            desc: "Complete legal verification and paperwork support for peace of mind."
        },
        {
            step: "05",
            title: "Closing Deal",
            desc: "Smooth transaction completion with registration and handover."
        }
    ];

    async function handleContactSubmit(e) {
        e.preventDefault();
        if (!canSubmitEnquiryForm(callbackForm)) return;

        const phone = sanitizeIndianMobileInput(callbackForm.phone);
        setEnquirySubmitting(true);
        try {
            await submitEnquiry({
                name: callbackForm.name.trim(),
                email: callbackForm.email.trim(),
                phone,
                property:
                    interest && interest !== "I'm interested in"
                        ? interest
                        : "General enquiry",
                message: (callbackForm.message || "").trim(),
                source: "Services page — Contact Us",
                submittedAt: new Date().toISOString(),
            });
            setContactOpen(false);
            setCallbackForm({ name: "", email: "", phone: "", message: "" });
            setInterest("I'm interested in");
        } catch (error) {
            console.error("Services enquiry failed:", error);
            alert(
                error.message ||
                    "Sorry, we could not submit your request. Please try again."
            );
        } finally {
            setEnquirySubmitting(false);
        }
    }

    function openContactModal(serviceName) {
        const interestMap = {
            "Property Buying Advisory": "Buying",
            "Property Selling": "Selling",
            "Investment Advisory": "Investment",
        };
        setInterest(interestMap[serviceName] || "I'm interested in");
        setContactOpen(true);
    }

    return (
        <div className="min-h-screen font-sans text-slate-800 dark:text-slate-100 overflow-x-hidden bg-white dark:bg-slate-950">
            <Navbar />

            {/* HERO SECTION */}
            <section
                className="
    pt-20 sm:pt-24 lg:pt-0 relative min-h-screen
    flex items-center justify-center
    overflow-hidden
    bg-gradient-to-br from-[#2D1B7E] via-[#3B2B9A] to-[#5A47D8]
  "
            >
                {/* Animated Background Elements */}
                <div className="absolute inset-0 opacity-20">
                    <div className="absolute top-10 sm:top-20 left-6 sm:left-10 w-40 sm:w-64 h-40 sm:h-64 bg-[#D83B29] rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-10 sm:bottom-20 right-6 sm:right-10 w-56 sm:w-80 h-56 sm:h-80 bg-[#8B1E1E] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                </div>

                {/* Grid Pattern Overlay */}
                <div
                    className="absolute inset-0 opacity-5"
                    style={{
                        backgroundImage:
                            `linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px),
         linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)`,
                        backgroundSize: '50px 50px',
                    }}
                ></div>

                {/* Content */}
                <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
                    {/* Top Badge */}
                    <div
                        className="
        inline-flex items-center gap-2
        px-4 sm:px-5 py-2
        rounded-full
        bg-white/10 backdrop-blur-md
        border border-white/20
        mb-6
      "
                        data-aos="fade-down"
                    >
                        <FaShieldAlt className="text-amber-300 text-sm sm:text-base" />
                        <span className="text-white/90 text-xs sm:text-sm font-medium">
                            Your Trusted Real Estate Partner
                        </span>
                    </div>

                    {/* Main Heading */}
                    <h1
                        className="
        text-3xl sm:text-4xl md:text-5xl lg:text-6xl
        font-extrabold text-white
        leading-tight
        mb-4
      "
                        data-aos="fade-up"
                        data-aos-delay="100"
                    >
                        Complete Real Estate
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-orange-300 to-[#D83B29]">
                            Services Under One Roof
                        </span>
                    </h1>

                    {/* Subtitle */}
                    <p
                        className="
        text-base sm:text-lg md:text-xl
        text-white/80
        max-w-xl sm:max-w-2xl
        mx-auto mb-8
        leading-relaxed
      "
                        data-aos="fade-up"
                        data-aos-delay="200"
                    >
                        Expert guidance for buying, selling, and investing in properties with
                        complete legal and documentation support
                    </p>

                    {/* CTA */}
                    <div
                        className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-10 sm:mb-12"
                        data-aos="fade-up"
                        data-aos-delay="300"
                    >
                        <button
                            onClick={() => setContactOpen(true)}
                            className="
          group
          px-6 sm:px-8 py-3.5
          bg-gradient-to-r from-[#D83B29] to-[#8B1E1E]
          text-white font-semibold
          rounded-full
          shadow-lg hover:shadow-2xl
          hover:scale-105
          transition-all duration-300
          flex items-center gap-2
        "
                        >
                            Schedule Free Consultation
                            <svg
                                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                            </svg>
                        </button>
                    </div>

                    {/* Service Quick Links */}
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 max-w-5xl mx-auto"
                        data-aos="fade-up"
                        data-aos-delay="400"
                    >
                        {[
                            { icon: <FaHome />, label: "Buy" },
                            { icon: <FaKey />, label: "Sell" },
                            { icon: <FaChartLine />, label: "Invest" },
                            { icon: <FaFileContract />, label: "Legal" },
                            { icon: <FaMapMarkedAlt />, label: "Survey" },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="
            group
            bg-white/10 backdrop-blur-md
            border border-white/20
            rounded-xl sm:rounded-2xl
            p-3 sm:p-4
            flex items-center gap-3 justify-center
            hover:bg-white/20 hover:border-white/40
            hover:scale-105
            transition-all duration-300
            cursor-pointer
          "
                                onClick={() =>
                                    document
                                        .querySelector("#core-services")
                                        ?.scrollIntoView({ behavior: "smooth" })
                                }
                            >
                                <div className="text-xl sm:text-2xl text-white/80 group-hover:text-white transition-transform group-hover:scale-110">
                                    {item.icon}
                                </div>
                                <div className="text-xs sm:text-sm font-medium text-white/70 group-hover:text-white">
                                    {item.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom Wave */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg
                        className="w-full h-14 sm:h-20 md:h-24"
                        preserveAspectRatio="none"
                        viewBox="0 0 1200 120"
                        fill="none"
                    >
                        <path
                            d="M0 0L50 10C100 20 200 40 300 46.7C400 53 500 47 600 43.3C700 40 800 40 900 46.7C1000 53 1100 67 1150 73.3L1200 80V120H0V0Z"
                            fill="white"
                        />
                    </svg>
                </div>
            </section>


            {/* MAIN SERVICES */}
            <section id="core-services" className="pt-12 pb-20 bg-white dark:bg-slate-950">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 inline-block relative"
                            data-aos="fade-up"
                        >
                            Core Services
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                        <p
                            className="text-slate-600 dark:text-slate-300 mt-6 max-w-2xl mx-auto"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            Our End-to-End Real Estate Services For All Your Property Needs
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {mainServices.map((service, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="group bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg border border-slate-100 dark:border-slate-600
                                    hover:shadow-2xl hover:-translate-y-2 transition-all duration-300"
                            >
                                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] 
                                    text-white grid place-items-center text-2xl mb-6
                                    group-hover:scale-110 transition-transform duration-300">
                                    {service.icon}
                                </div>

                                <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-3">
                                    {service.title}
                                </h3>

                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                                    {service.desc}
                                </p>

                                <ul className="space-y-2 mb-6">
                                    {service.features.map((feature, i) => (
                                        <li key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                                            <span className="text-[#D83B29] mt-1">✓</span>
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    onClick={() => openContactModal(service.title)}
                                    className="w-full py-2.5 rounded-xl bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8]
                                        text-white font-medium shadow-md hover:shadow-lg
                                        transition-all duration-300 group-hover:scale-105"
                                >
                                    Get Started
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROCESS SECTION */}
            <section className="py-12 sm:py-16 lg:py-20 bg-slate-50 dark:bg-slate-950">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12 sm:mb-16">
                        <h2
                            className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 inline-block relative"
                            data-aos="fade-up"
                        >
                            Our Simplified Process
                            <span className="absolute left-1/2 -bottom-2 sm:-bottom-3 -translate-x-1/2 w-16 sm:w-20 h-0.5 sm:h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                        <p
                            className="text-slate-600 dark:text-slate-300 mt-4 sm:mt-6 text-sm sm:text-base"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            A Right Path to Your Journey from Search to Ownership
                        </p>
                    </div>

                    <div className="relative">
                        {/* Desktop Horizontal Line */}
                        <div className="hidden lg:block absolute top-[40px] left-0 right-0 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8]"></div>

                        {/* Mobile/Tablet Vertical Line */}
                        <div className="lg:hidden absolute left-[30px] sm:left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-[#3B2B9A] to-[#5A47D8] sm:-translate-x-1/2"></div>

                        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-5 gap-6 sm:gap-8 relative">
                            {process.map((item, index) => (
                                <div
                                    key={index}
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                    className="relative flex sm:flex-col gap-4 sm:gap-0"
                                >
                                    {/* Step Number Circle */}
                                    <div className="flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 sm:mx-auto rounded-full bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] text-white font-bold text-xl sm:text-2xl grid place-items-center shadow-lg sm:mb-4 relative z-10">
                                        {item.step}
                                    </div>

                                    {/* Content Card */}
                                    <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl p-4 sm:p-6 shadow-md sm:text-center self-center sm:self-auto border border-slate-100 dark:border-slate-600">
                                        <h3 className="font-bold text-base sm:text-lg text-slate-800 dark:text-slate-100 mb-1 sm:mb-2">
                                            {item.title}
                                        </h3>
                                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                            {item.desc}
                                        </p>
                                    </div>

                                    {/* Mobile Arrow Connector (between steps) */}
                                    {/* {index < process.length - 1 && (
                                        <div className="lg:hidden absolute left-[17px] sm:left-1/2 bottom-[-10px] sm:bottom-[-32px] sm:-translate-x-1/2 z-10">
                                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white dark:bg-slate-900 border-2 border-[#3B2B9A] flex items-center justify-center shadow-md">
                                                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-[#3B2B9A]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </div>
                                    )} */}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile Timeline Alternative Design */}
                    <style>{`
                    @media (max-width: 639px) {
                        /* Enhance mobile experience with pulse animation */
                        .relative > div:nth-child(odd) .bg-gradient-to-br {
                            animation: pulse-subtle 3s ease-in-out infinite;
                        }
                    }
                    
                    @keyframes pulse-subtle {
                        0%, 100% { 
                            box-shadow: 0 10px 15px -3px rgba(59, 43, 154, 0.1);
                        }
                        50% { 
                            box-shadow: 0 10px 25px -3px rgba(59, 43, 154, 0.3);
                        }
                    }
                `}</style>
                </div>
            </section>

            {/* ADDITIONAL SERVICES */}
            <section className="py-20 bg-white dark:bg-slate-950">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 inline-block relative"
                            data-aos="fade-up"
                        >
                            Additional Services
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                        <p
                            className="text-slate-600 dark:text-slate-300 mt-6"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            Our Extended Support Services For a Complete Property Guidance
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 place-items-center">
                        {additionalServices.map((service, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="bg-gradient-to-br from-slate-50 to-white dark:from-slate-800 dark:to-slate-900 rounded-xl p-6
                                    border border-slate-200 dark:border-slate-600 hover:border-[#3B2B9A]/30
                                    hover:shadow-lg transition-all duration-300 text-center group"
                            >
                                <div className="w-14 h-14 mx-auto rounded-xl bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8]
                                    text-white grid place-items-center text-xl mb-4
                                    group-hover:scale-110 transition-transform">
                                    {service.icon}
                                </div>
                                <h3 className="font-bold text-slate-800 dark:text-slate-100 mb-2">
                                    {service.title}
                                </h3>
                                <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                    {service.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA SECTION */}
            <section className="py-20 bg-slate-100 dark:bg-slate-800">
                <div className="max-w-5xl mx-auto px-6">
                    <div className="relative bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-3xl p-12 md:p-16 overflow-hidden shadow-2xl">
                        {/* Decorative Elements */}
                        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#D83B29]/20 rounded-full blur-3xl"></div>

                        {/* Pattern Overlay */}
                        <div className="absolute inset-0 opacity-5" style={{
                            backgroundImage: `linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)`,
                            backgroundSize: '40px 40px'
                        }}></div>

                        <div className="relative z-10 text-center">
                            {/* Icon */}
                            <div
                                className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6"
                                data-aos="fade-up"
                            >
                                <FaUserTie className="text-3xl text-white" />
                            </div>

                            <h2
                                className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight"
                                data-aos="fade-up"
                                data-aos-delay="100"
                            >
                                Ready to Find Your
                                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-300">
                                    Perfect Property?
                                </span>
                            </h2>

                            <p
                                className="text-white/90 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed"
                                data-aos="fade-up"
                                data-aos-delay="200"
                            >
                                Let our expert team guide you through every step of your property journey. Get personalized consultation at zero cost.
                            </p>

                            <div
                                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                                data-aos="fade-up"
                                data-aos-delay="300"
                            >
                                <button
                                    onClick={() => setContactOpen(true)}
                                    className="group px-8 py-4 bg-white dark:bg-slate-100 text-[#3B2B9A] dark:text-violet-900 font-bold rounded-full
                            shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-300 
                            flex items-center gap-2"
                                >
                                    Schedule Free Consultation
                                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </button>

                                <a
                                    href={LANDMARKS_PHONE_TEL}
                                    onClick={openTelDialer}
                                    className="px-8 py-4 bg-white/10 backdrop-blur-md text-white font-semibold rounded-full
    border-2 border-white/30 hover:bg-white/20 hover:border-white/50
    transition-all duration-300 flex items-center gap-2"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                                        />
                                    </svg>
                                    Call Us Now
                                </a>
                            </div>

                            {/* Trust Indicators */}
                            <div className="mt-12 pt-8 border-t border-white/20">
                                <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
                                    <div className="text-center">
                                        <div className="text-2xl md:text-3xl font-bold text-white mb-1">24/7</div>
                                        <div className="text-sm text-white/70">Support</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl md:text-3xl font-bold text-white mb-1">100%</div>
                                        <div className="text-sm text-white/70">Legal</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl md:text-3xl font-bold text-white mb-1">Free</div>
                                        <div className="text-sm text-white/70">Consultation</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            {/* CONTACT MODAL */}
            {contactOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 opacity-100 transition-opacity duration-300"
                        onClick={() => setContactOpen(false)}
                    ></div>

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <form
                            onSubmit={handleContactSubmit}
                            className="lr-callback-panel animate-[fadeInScale_0.35s_ease-out]"
                        >
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setContactOpen(false)}
                                className="
                                    absolute -top-3 -right-3
                                    bg-white dark:bg-slate-900 backdrop-blur 
                                    text-gray-700 dark:text-gray-300
                                    shadow-md hover:shadow-lg
                                    w-9 h-9 flex items-center justify-center
                                    rounded-full border border-gray-200
                                    transition-all duration-300
                                    hover:scale-110 hover:rotate-90
                                "
                            >
                                ✕
                            </button>

                            <h3 className="text-2xl font-extrabold text-[#2D1B7E] dark:text-indigo-200 tracking-tight">
                                Request a Callback
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                Share your details and our team will call you within one business day.
                            </p>

                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    name="name"
                                    autoComplete="name"
                                    value={callbackForm.name}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="Full name"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={callbackForm.email}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    placeholder="Email address"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={callbackForm.phone}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            phone: sanitizeIndianMobileInput(
                                                e.target.value
                                            ),
                                        }))
                                    }
                                    placeholder="Mobile (10 digits, starts 6–9)"
                                    className="lr-callback-field"
                                />

                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => setInterestOpen(!interestOpen)}
                                        className="lr-callback-field flex items-center justify-between"
                                    >
                                        <span className={interest === "I'm interested in" ? "text-slate-400" : "text-slate-700 dark:text-slate-200"}>
                                            {interest}
                                        </span>

                                        <svg className={`w-4 h-4 transition-transform ${interestOpen ? "rotate-180" : ""}`}
                                            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {interestOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl z-50 overflow-hidden animate-fadeIn">
                                            {["Buying", "Selling", "Investment"].map((item) => (
                                                <div
                                                    key={item}
                                                    className="px-4 py-2 hover:bg-[#D83B29]/10 cursor-pointer text-slate-700 dark:text-slate-200"
                                                    onClick={() => {
                                                        setInterest(item);
                                                        setInterestOpen(false);
                                                    }}
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <textarea
                                name="message"
                                rows="4"
                                value={callbackForm.message}
                                onChange={(e) =>
                                    setCallbackForm((prev) => ({
                                        ...prev,
                                        message: e.target.value,
                                    }))
                                }
                                placeholder="Message (optional)"
                                className="lr-callback-field resize-none"
                            ></textarea>

                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    type="submit"
                                    disabled={!callbackFormValid || enquirySubmitting}
                                    className="
        relative overflow-hidden group
        px-6 py-3 rounded-xl text-white font-semibold
        bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
        shadow-lg hover:shadow-xl
        hover:brightness-105 transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100
    "
                                >
                                    {enquirySubmitting ? "Sending..." : "Submit Request"}

                                    <span
                                        className="
            absolute inset-0 bg-white/20
            translate-x-[-120%]
            group-hover:translate-x-[120%]
            transition-all duration-700 ease-in-out
            skew-x-[45deg]
        "
                                    ></span>
                                </button>

                                <small className="text-gray-600 dark:text-gray-400">We respect your privacy.</small>
                            </div>
                        </form>
                    </div>

                    <style>
                        {`
                @keyframes fadeInScale {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}
                    </style>
                </>
            )}
        </div>
    );
}